/**
 * Сервис для работы с API
 * 
 * Отвечает за:
 * - Создание и настройку HTTP-клиента
 * - Взаимодействие с API для получения, создания, обновления и удаления контента
 * - Загрузку файлов
 */
import axios, { AxiosInstance } from 'axios';
import { BASE_URL, SOCKET_URL } from '../utils/constants';
import { getCookie, setCookie } from '../utils/cookieUtils';
import { clearTokens } from '../utils/authUtils';
import type { IAdmin, ISteramMessage, IStreamChat, IUser, ITraining, INotification, IStreamRecording, IRecordingResPub } from '../types';

// Минимальное время до истечения refresh токена для обновления (1 день)
const REFRESH_TOKEN_EXPIRY_THRESHOLD = 24 * 60 * 60; // в секундах
// Минимальное время до истечения access токена для обновления (5 минут)
const ACCESS_TOKEN_REFRESH_THRESHOLD = 5 * 60; // в секундах
// Интервал обновления токена (в миллисекундах) - ровно 50 минут
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000;

type VideoType = 'training' | 'bonus' | 'instruction' | 'presentation' | 'free';

interface ProgressPhoto {
  url: string;
  language: string;
  id: number;
}

/**
 * Интерфейс данных статистики пользователя с API
 */
export interface UserStatsAPI {
  id: number;
  name: string;
  email: string | null;
  is_active: boolean;
  registration_date: string | null;
  first_login_date: string | null;
  last_login_date: string | null;
  subscription_status: string | null;
  subscription_end_date: string | null;
  completed_trainings_count: number;
  completed_trainings_last_30_days: number;
  total_trainings_count: number;
  language: string;
}

/**
 * Класс для работы с API приложения
 */
export class ApiService {
  private api: AxiosInstance;
  private currentLanguage: string;
  private refreshTimer: NodeJS.Timeout | null = null;
  private lastRefreshTime: number = 0;

  /**
   * Конструктор сервиса API
   * 
   * Создает и настраивает HTTP-клиент с базовым URL
   */
  constructor() {
    // Инициализируем язык из cookie или localStorage или используем русский по умолчанию
    this.currentLanguage = getCookie('language') || getCookie('i18next') || localStorage.getItem('i18nextLng') || 'ru';

    this.api = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Перехватчик запросов для добавления токена авторизации
    this.api.interceptors.request.use((config) => {
      const token = getCookie('access_token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    // Перехватчик ответов для обработки 401 ошибок
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Удаляем токены из куки
          clearTokens();
          // Перенаправляем на страницу логина
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Запускаем таймер обновления токена если есть refresh токен
    this.setupRefreshTimer();
  }

  /**
   * Настраивает таймер для периодического обновления токена
   */
  private setupRefreshTimer(): void {
    // Останавливаем предыдущий таймер если он был
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Проверяем наличие refresh токена
    const refreshToken = getCookie('refresh_token');
    if (!refreshToken) return;

    // Проверяем время последнего успешного обновления токена
    const lastLoginTime = localStorage.getItem('last_login_time');
    if (lastLoginTime) {
      this.lastRefreshTime = parseInt(lastLoginTime, 10);
    }

    // Запускаем таймер на фиксированный интервал в 50 минут
    this.refreshTimer = setTimeout(() => {
      this.refresh().then(success => {
        if (success) {
          // Сохраняем время последнего успешного обновления
          this.lastRefreshTime = Date.now();
          localStorage.setItem('last_login_time', this.lastRefreshTime.toString());
        }
        // В любом случае перезапускаем таймер
        this.setupRefreshTimer();
      });
    }, TOKEN_REFRESH_INTERVAL);
  }

  /**
   * Проверяет, подходит ли access токен к концу срока действия
   * @returns {boolean} true, если access токен скоро истечет
   */
  private isAccessTokenExpiring(): boolean {
    const expiresAt = getCookie('token_expires_at');
    if (!expiresAt) return false;

    const timeLeft = parseInt(expiresAt, 10) - Date.now();
    // Обновляем, если до истечения осталось меньше порога
    return timeLeft > 0 && timeLeft < ACCESS_TOKEN_REFRESH_THRESHOLD * 1000;
  }

  /**
   * Проверяет, подходит ли refresh токен к концу срока действия
   * @returns {boolean} true, если токен скоро истечет
   */
  private isRefreshTokenExpiring(): boolean {
    const refreshToken = getCookie('refresh_token');
    if (!refreshToken) return false;

    // Получаем время создания refresh токена из свойств куки
    const cookies = document.cookie.split(';');
    const refreshTokenCookie = cookies.find(cookie => cookie.trim().startsWith('refresh_token='));

    if (!refreshTokenCookie) return false;

    // В браузере можно получить время истечения куки
    // Предполагаем, что refresh токен хранится 30 дней
    const now = new Date().getTime();
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 дней в миллисекундах
    const estimatedCreationTime = this.getCookieCreationTime('refresh_token');

    if (!estimatedCreationTime) return false;

    const estimatedExpiryTime = estimatedCreationTime + maxAge;
    const timeLeft = estimatedExpiryTime - now;

    // Если осталось меньше порога, токен скоро истечет
    return timeLeft > 0 && timeLeft < REFRESH_TOKEN_EXPIRY_THRESHOLD * 1000;
  }

  /**
   * Метод для обновления токена авторизации
   * @returns {Promise<boolean>} Результат обновления токена
   */
  async refresh(): Promise<boolean> {
    const refreshToken = getCookie('refresh_token');

    // Не выполняем запрос, если refresh токена нет
    if (!refreshToken) return false;

    try {
      const response = await this.api.post('/auth/refresh', { refresh_token: refreshToken });

      // Проверяем, что в ответе есть нужные токены
      if (response.data?.access_token && response.data?.refresh_token) {
        // Сохраняем новые токены
        const expiresIn = response.data.expires_in || 3600;

        // Сохраняем access token
        setCookie('access_token', response.data.access_token, expiresIn / 86400); // в днях

        // Сохраняем refresh token на более длительный срок
        setCookie('refresh_token', response.data.refresh_token, 30);

        // Сохраняем время истечения
        const expiresAt = Date.now() + (expiresIn * 1000);
        setCookie('token_expires_at', expiresAt.toString(), expiresIn / 86400);

        // Обновляем время последнего обновления
        this.lastRefreshTime = Date.now();
        localStorage.setItem('last_login_time', this.lastRefreshTime.toString());

        // Перезапускаем таймер обновления на следующие 50 минут
        this.setupRefreshTimer();

        return true;
      }
      return false;
    } catch (error) {
      console.error('Ошибка при обновлении токена:', error);
      return false;
    }
  }

  /**
   * Вспомогательная функция для получения примерного времени создания куки
   * @param name Имя куки
   * @returns {number|null} Время создания куки в миллисекундах или null
   */
  private getCookieCreationTime(name: string): number | null {
    const lastLoginTime = localStorage.getItem('last_login_time');
    if (lastLoginTime) {
      return parseInt(lastLoginTime, 10);
    }

    // Если нет сохраненного времени логина, используем текущее время
    // минус половина срока действия куки (примерное время создания)
    return Date.now() - (15 * 24 * 60 * 60 * 1000); // 15 дней назад
  }

  /**
   * Устанавливает текущий язык для API запросов
   * @param language - Код языка ('ru' или 'it')
   */
  setLanguage(language: string): void {
    this.currentLanguage = language;
  }

  /**
   * Возвращает текущий активный язык API
   */
  getLanguage(): string {
    return this.currentLanguage;
  }

  // Загрузка видео файла через body
  async uploadVideoFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/training/upload-video', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка превью файла через body
  async uploadPreviewFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/training/upload-preview', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка видео файла для бонусных видео
  async uploadBonusVideoFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/bonus/upload-video', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка превью файла для бонусных видео
  async uploadBonusPreviewFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/bonus/upload-preview', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка видео файла для бесплатного видео
  async uploadFreeVideoFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/free/upload-video', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка превью файла для бесплатного видео
  async uploadFreePreviewFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/free/upload-preview', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка видео файла для инструкции
  async uploadInstructionVideoFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/instruction/upload-video', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка превью файла для инструкции
  async uploadInstructionPreviewFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/instruction/upload-preview', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка видео файла для презентации
  async uploadPresentationVideoFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/presentation/upload-video', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }

  // Загрузка превью файла для презентации
  async uploadPresentationPreviewFile(file: File): Promise<{ file_path: string; message: string }> {
    const response = await this.api.post('/videos/presentation/upload-preview', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }



  // Загрузка файла (для превью или видео) - старый метод для совместимости
  async uploadFile(file: File, type: 'video' | 'preview') {
    const formData = new FormData();
    formData.append('file', file);

    // Изменяем URL в соответствии с API бэкенда
    const endpoint = type === 'video' ? '/videos/upload' : '/previews/upload';
    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Получение списка видео инструкций
  async getVideoList(type: VideoType): Promise<ITraining[]> {
    const response = await this.api.get(`/videos/${type}`);
    return response.data;
  }

  // Создание видео
  async uploadVideo(type: VideoType, data: any): Promise<ITraining> {
    // Все типы видео теперь используют новую схему JSON
    const response = await this.api.post(`/videos/${type}`, data, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  }

  // Удаление видео
  async deleteVideo(type: VideoType, id: number): Promise<ITraining> {
    const response = await this.api.delete(`/videos/${type}/${id}`);
    return response.data;
  }

  // Редактирование видео
  async editVideo(type: VideoType, id: number, data: any): Promise<ITraining> {
    // Бэкенд ожидает Form параметры, поэтому не устанавливаем Content-Type
    // Axios автоматически установит multipart/form-data для FormData
    const response = await this.api.patch(`videos/${id}`, data);
    return response.data;
  }

  // Получение списка фотографий прогресса
  async getProgressPhotos(): Promise<ProgressPhoto[]> {
    const response = await this.api.get('/progress-photos/');
    return response.data;
  }

  // Загрузка фотографии прогресса
  async uploadProgressPhoto(data: File | FormData): Promise<ProgressPhoto> {
    const formData = data instanceof File ? (() => {
      const fd = new FormData();
      fd.append('file', data);
      return fd;
    })() : data;

    const response = await this.api.post('/progress-photos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Обновление фотографии прогресса
  async updateProgressPhoto(id: number, data: File | FormData | { url?: string; language?: string }): Promise<ProgressPhoto> {
    const isFile = data instanceof File;
    const isFormData = data instanceof FormData;

    let requestData: any = data;
    if (isFile) {
      const formData = new FormData();
      formData.append('photo_file', data);
      requestData = formData;
    } else if (isFormData) {
      // Для FormData просто отправляем как есть, но убеждаемся что поле называется photo_file
      const file = data.get('file') as File;
      if (file) {
        const newFormData = new FormData();
        newFormData.append('photo_file', file);
        requestData = newFormData;
      }
    }

    const response = await this.api.patch(`/progress-photos/${id}`, requestData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Удаление фотографии прогресса
  async deleteProgressPhoto(id: number): Promise<ProgressPhoto> {
    const response = await this.api.delete(`/progress-photos/${id}`);
    return response.data;
  }



  /**
   * Сохраняет порядок видео
   * @param videos - Массив объектов с id, order и time
   */
  async saveVideosOrder(videos: Array<{ id: number; order: number; time: string }>) {
    return this.api.post('/videos/order', { videos });
  }

  /**
   * Получение списка уведомлений
   */
  async getNotifications(): Promise<INotification[]> {
    const response = await this.api.get('/notifications/');
    return response.data;
  }

  /**
   * Отправка уведомления
   */
  async sendNotification(payload: {
    title: string;
    body: string;
    type: string;
    language: string;
    scheduled_at?: string;
    consider_timezone?: boolean;
    active_subscriptions?: boolean;
    inactive_subscriptions?: boolean;
    is_active: boolean;
    data?: {
      screen?: string;
      tab?: string;
      external_link?: string;
      language?: string;
      params?: Record<string, any>;
    }; // Необязательное поле для навигационных данных
  }): Promise<INotification> {
    const response = await this.api.post('/notifications/', payload);
    return response.data;
  }

  /**
   * Удаление уведомления
   */
  async deleteNotification(id: number): Promise<void> {
    await this.api.delete(`/notifications/${id}`);
  }

  /**
   * Обновление версий приложения для платформы (iOS / Android)
   */
  async updateAppVersion(
    platform: string,
    data: { minimum_version: string; latest_version: string; store_url: string; release_notes: string }
  ): Promise<{
    platform: string;
    minimum_version: string;
    latest_version: string;
    store_url: string;
    release_notes: string;
    updated_at: string;
  }> {
    const response = await this.api.put(`/app/versions/${platform.toLowerCase()}`, data);
    return response.data;
  }

  /**
   * Получение статистики пользователей
   * @returns Promise с данными статистики пользователей
   */
  async getUsersStatistics(): Promise<UserStatsAPI[]> {
    const response = await this.api.get<UserStatsAPI[]>('/users/statistics');
    return response.data;
  }

  /**
   * Запуск стрима
   */
  async streamStart(): Promise<void> {
    const response = await this.api.post('/streams/start');
    return response.data;
  }

  /**
   * Остановка стрима
   */
  async streamStop(chat: IStreamChat): Promise<void> {
    const response = await this.api.post('/streams/stop', chat);
    return response.data;
  }

  /**
   * Статус стрима
   */
  async streamStatus(): Promise<{ is_active: boolean }> {
    const response = await this.api.get('/streams/status');
    return response.data;
  }

  /**
   * Количество зрителей в комнате стрима
   * roomId — id комнаты, viewers — зрители (все минус ведущий), totalPeers — всего участников, streamActive — идёт ли стрим
   */
  async getRoomViewers(roomId: string): Promise<{ roomId: string; viewers: number; totalPeers: number; streamActive: boolean }> {
    const response = await this.api.get(`${SOCKET_URL}/video-api/api/room/${roomId}/viewers`);
    return response.data;
  }

  /**
   * Получение сохраненных чатов стрима
   */
  async streamChat(limit: number = 100): Promise<ISteramMessage[]> {
    const response = await this.api.get<ISteramMessage[]>(`/streams/chat/history?limit=${limit}`);
    return response.data
  }

  /**
   * Информация о стриме и история сообщений чата
   */
  async streamInfo(): Promise<void> {
    const response = await this.api.get('/streams');
    return response.data
  }

  /**
   * Возвращает информацию об администраторе
   */
  async adminInfo(): Promise<IAdmin> {
    const response = await this.api.get<IAdmin>('/auth/admin-info');
    return response.data;
  }

  /**
   * Возвращает информацию о пользователе
   */
  async adminCheck(): Promise<IUser> {
    const response = await this.api.get<IUser>('/users/check-admin');
    return response.data;
  }

  // Добавлено: получение записей стримов
  async getStreamRecordings(): Promise<IStreamRecording[]> {
    const response = await this.api.get('/streams/recordings');
    return response.data;
  }

  // Добавлено: удаление записи стрима (бакет + БД обрабатывает бэкенд)
  async deleteStreamRecording(id: number): Promise<void> {
    await this.api.delete(`/streams/recordings/${id}`);
  }

  // Добавлено: публикация записи стрима в тренировки
  async publishStreamRecording(recordingId: number): Promise<IRecordingResPub> {
    const response = await this.api.post(`/streams/recordings/${recordingId}/convert-to-training`);
    return response.data;
  }

  // Методы для отслеживания статуса обработки видео
  async getVideoProcessingStatus(videoId: number): Promise<any> {
    const response = await this.api.get(`/media-tasks/video/${videoId}/status`);
    return response.data;
  }

  async getMediaTaskStatus(taskId: string): Promise<any> {
    const response = await this.api.get(`/media-tasks/status/${taskId}`);
    return response.data;
  }

  /**
   * Импорт пользователей из Excel файла
   * @param file - Excel файл для импорта
   * @param language - Язык для импорта
   */
  async importUsersFromExcel(file: File, language: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);

    const response = await this.api.post('/users/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Обновление даты окончания подписки пользователя
   * @param userId - ID пользователя
   * @param language - Язык пользователя
   * @param expirationDate - Новая дата окончания подписки
   */
  async updateSubscriptionExpiration(userId: number, language: string, expirationDate: string): Promise<any> {
    const response = await this.api.patch('/users/subscription/update-expiration', {
      id: userId,
      language: language,
      expiration_date: expirationDate
    });
    return response.data;
  }

  /**
   * Создание нового пользователя
   * @param payload - данные нового пользователя
   */
  async createUser(payload: {
    email: string;
    name: string;
    language: string;
    subscription_expiration_date: string;
  }): Promise<any> {
    const response = await this.api.post('/users/create', payload);
    return response.data;
  }

}

export const apiService = new ApiService();